import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type PersonModel from 'tio-common/models/person';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type PslfGroup from 'tio-common/models/pslf-group';
import type RecommendationModel from 'tio-common/models/recommendation';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from 'tio-employee/services/session-context';
import type FeaturesService from 'tio-common/services/features';
import type PslfDebugService from 'tio-employee/services/pslf-debug';

export default class AuthenticatedPslfDashboardIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare pslfDebug: PslfDebugService;

  queryParams = {
    v2: { refreshModel: true },
  };

  async model(params: { v2?: string }) {
    const isV2 = params.v2 === 'true' && this.features.isEnabled('pslfReimaginedDashboard');
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id;
    const parentModel = this.modelFor('authenticated.pslf') as {
      pslfProfile: PslfProfileModel;
      groups: PslfGroup[];
    };

    const [loanSummary, person, mostRecentRecQuery] = await Promise.all([
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      this.store.adapterFor('user').getLoanSummary(userId),
      // @ts-expect-error type models
      this.store.findRecord<PersonModel>('person', personId, {
        include: 'accounts.financial-institution,accounts.loans',
        reload: true,
      }),
      this.store.query<RecommendationModel>('recommendation', {
        filter: {
          user: userId,
          'requesting-app': 'PSLF',
        },
        include: 'user',
        sort: '-created-at',
        page: {
          offset: 0,
          limit: 1,
        },
      }),
    ]);

    const baseModel = {
      accounts: person.accounts,
      loanSummary: loanSummary,
      mostRecentRecommendation: mostRecentRecQuery?.[0] as RecommendationModel | undefined,
      person: person,
      pslfProfile: parentModel.pslfProfile,
      pslfForms: [] as PslfFormModel[],
      isV2,
      debugLinkLoans: false,
    };

    if (!isV2) {
      return baseModel;
    }

    // v2 mode: fetch PSLF forms
    const pslfProfile = parentModel.pslfProfile;
    const pslfForms = await this.store.query<PslfFormModel>('pslf-form', {
      filter: { 'pslf-profile': pslfProfile.id },
      include: 'attachments',
    });

    baseModel.pslfForms = pslfForms.slice() as PslfFormModel[];

    // Apply PSLF debug overrides (feature-flagged; dead code in production)
    if (this.features.isEnabled('pslfDebugPanel') && this.pslfDebug.isActive()) {
      return this.pslfDebug.applyTo(baseModel);
    }

    return baseModel;
  }
}
